import { useTonConnectUI } from "@tonconnect/ui-react"
import { ReactElement } from "react"
import { useTranslation } from "react-i18next"
import { showToast } from "@/helpers/toast"
import { useModal } from "@/modals"
import { TonConnectProvider } from "@/providers/TonConnectProvider"
import { useGetUserDataQuery } from "@/store/apis/userAPI"
import { useDisconnectWalletMutation } from "@/store/apis/withdrawalAPI"
import { Button } from "@/ui/Button"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./DisconnectWalletModal.module.scss"

const modalType = "disconnectWallet" as const

function DisconnectWalletModalInner(): ReactElement {
  const { t } = useTranslation()
  const { modal } = useModal(modalType)
  const [tonConnectUI] = useTonConnectUI()
  const { walletAddress } = useGetUserDataQuery(undefined, {
    selectFromResult: ({ data }) => ({
      walletAddress: data?.wallets?.telegram ?? ""
    })
  })
  const [disconnectWallet, { isLoading: isProcessing }] =
    useDisconnectWalletMutation()

  async function handleConfirm(): Promise<void> {
    try {
      // Step 1: backend is the source of truth — disconnect there first.
      await disconnectWallet({ walletType: "telegram" }).unwrap()
      // Step 2: drop the local TonConnect session only after backend success.
      if (tonConnectUI.connected) {
        await tonConnectUI.disconnect()
      }
      modal.close()
    } catch {
      showToast.error(t("wallet.withdraw.disconnectModal.error"))
    }
  }

  return (
    <Modal.Center type={modalType}>
      <Modal.StoneFlexContainer
        content={{ className: styles.DisconnectWalletModal_content }}
      >
        <OutlinedText className={styles.DisconnectWalletModal_title}>
          {t("wallet.withdraw.disconnectModal.title")}
        </OutlinedText>
        <div
          className={styles.DisconnectWalletModal_address}
          aria-label={t("wallet.withdraw.disconnectModal.addressLabel")}
        >
          {walletAddress}
        </div>
        <div className={styles.DisconnectWalletModal_buttons}>
          <Button
            type="button"
            className={styles.DisconnectWalletModal_buttons_item}
            styles="green"
            onClick={() => modal.close()}
            disabled={isProcessing}
          >
            {t("wallet.withdraw.disconnectModal.cancel")}
          </Button>
          <Button
            type="button"
            className={styles.DisconnectWalletModal_buttons_item}
            styles="red"
            onClick={handleConfirm}
            isLoading={isProcessing}
          >
            {t("wallet.withdraw.disconnectModal.confirm")}
          </Button>
        </div>
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}

// Modals render outside the Router tree (App.tsx: <Modals /> is a sibling of
// <Router />), so the route-scoped TonConnectProvider from /wallet does not
// reach this component. Wrapping here is required — same pattern as BuyGoldCard.
export function DisconnectWalletModal(): ReactElement {
  return (
    <TonConnectProvider>
      <DisconnectWalletModalInner />
    </TonConnectProvider>
  )
}
